<script lang="ts">import { cfgBrandTitle, cfgDiscordLink, cfgFacebookLink, cfgInstagramLink, cfgTelegramLink, cfgToThemeCdnPath, cfgToThemeContentPath, cfgXLink, cfgYoutubeLink, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { closeThisModal, handleCustomerSupport, isMobile, locale, ModalDialog, modalStack, pushModal } from "@infinitro/global";
import { DiscordIcon, FacebookIcon, InstagramIcon, TelegramIcon, XIcon, YoutubeIcon } from "@infinitro/icons";
import { Modal } from "@infinitro/modal";
import { Text } from "@infinitro/text";
import MarkdownIt from "markdown-it";
import { onMount } from "svelte";
import { derived } from "svelte/store";
const thisModal = ModalDialog.Maintenance;
const closeModal = () => closeThisModal(thisModal);
const modalIndex = derived(modalStack, ($modalStack) => $modalStack.findIndex((e) => e.modal === thisModal));
const zOverlay = derived(modalIndex, ($modalIndex) => $modalIndex >= 0 ? 9999 + $modalIndex * 2 : 9999);
$: useUppercaseBrandTitle = $stateUi.maintenance?.useUppercaseBrandTitle === true;
$: BRAND_TITLE = useUppercaseBrandTitle ? cfgBrandTitle().toUpperCase() : cfgBrandTitle();
const illustrationUrl = cfgToThemeCdnPath(`/icons/maintenance-icon.png`);
const md = new MarkdownIt({
	html: true,
	breaks: true,
	linkify: true
});
const X_LINK = cfgXLink();
const DISCORD_LINK = cfgDiscordLink();
const TELEGRAM_LINK = cfgTelegramLink();
const FACEBOOK_LINK = cfgFacebookLink();
const INSTAGRAM_LINK = cfgInstagramLink();
const YOUTUBE_LINK = cfgYoutubeLink();
const socialMediaConfig = [
	{
		name: "Discord",
		icon: DiscordIcon,
		link: DISCORD_LINK
	},
	{
		name: "X",
		icon: XIcon,
		link: X_LINK
	},
	{
		name: "Telegram",
		icon: TelegramIcon,
		link: TELEGRAM_LINK
	},
	{
		name: "Facebook",
		icon: FacebookIcon,
		link: FACEBOOK_LINK
	},
	{
		name: "Instagram",
		icon: InstagramIcon,
		link: INSTAGRAM_LINK
	},
	{
		name: "YouTube",
		icon: YoutubeIcon,
		link: YOUTUBE_LINK
	}
];
const socialMedia = socialMediaConfig.filter((item) => item.link);
let markdownContentPath = "";
let htmlContent = "";
let markdownContainer = null;
$: maintenanceStyle = $stateUi.maintenance?.maintenanceStyle ?? "default";
$: useAlternative2 = maintenanceStyle === "alternative2";
$: useMarkdownContent = $stateUi.maintenance?.contentSource === "markdown";
async function fetchContent(isFallback = false) {
	try {
		const response = await fetch(markdownContentPath);
		if (!response.ok) throw new Error(`Error fetching content: ${response.statusText}`);
		let text = await response.text();
		text = text.replace(/%(domain|brand)%/g, BRAND_TITLE);
		htmlContent = md.render(text);
	} catch (error) {
		console.error("Failed to fetch maintenance content:", error);
		if (!isFallback) {
			markdownContentPath = cfgToThemeContentPath(`/maintenance/maintenance.en.md`);
			await fetchContent(true);
		} else {
			htmlContent = "";
		}
	}
}
function onMarkdownClick(event) {
	const target = event.target;
	const link = target.closest("a");
	if (!link || !markdownContainer?.contains(link)) return;
	const href = link.getAttribute("href") ?? "";
	const isSupportLink = href === "#" || href === "support://intercom" || href === "intercom://support";
	if (isSupportLink) {
		event.preventDefault();
		handleCustomerSupport();
	}
}
$: {
	if (useMarkdownContent) {
		markdownContentPath = cfgToThemeContentPath(`/maintenance/maintenance.[locale].md`).replace("[locale]", $locale);
		fetchContent();
	} else {
		htmlContent = "";
	}
}
onMount(() => {
	pushModal(ModalDialog.Maintenance);
});
</script>

<Modal
	title={null}
	{closeModal}
	width="100%"
	height="85vh"
	maxWidth="64rem"
	maxHeight="85vh"
	maxHeightContent="calc(85vh - 95px)"
	padding=""
	closeByBackdrop={true}
	showCloseButton={true}
	alwaysCentered={false}
	notFullScreenMobile={false}
	zIndexOverlay={$zOverlay}
	zIndexUnderlay={$zOverlay - 1}
>
	<div class="content">
		<img
			src={illustrationUrl}
			alt={$t(Verbiage.maintenance_alt, 'alts')}
			class={$isMobile ? 'icon-mobile' : 'icon'}
		/>

		{#if useMarkdownContent && htmlContent}
			<div class="maintenance-markdown" bind:this={markdownContainer} on:click={onMarkdownClick}>
				{@html htmlContent}
			</div>
		{:else}
			<Text type="title" titleSize="h4" textColor="white" bodySize="small" textAlign={'center'}>
				{$t(Verbiage.maintenance_title)}
			</Text>

			<Text type="body" titleSize="h5" textColor="white" bodySize="small" textAlign="center">
				{$t(Verbiage.maintenance_description_intro, 'global', { BRAND: BRAND_TITLE })}
				<span class="bold"> {$t(Verbiage.maintenance_description_outro)}</span>
			</Text>

			<Text type="body" titleSize="h5" textColor="white" bodySize="small" textAlign={'center'}>
				{$t(Verbiage.maintenance_support)}
				<button on:click={handleCustomerSupport} class="highlight"
					>{$t(Verbiage.maintenance_contact_support)}</button
				>
			</Text>
		{/if}

		<Text type="body" titleSize="h5" textColor="gray" bodySize="small" textAlign={'center'}>
			{$t(Verbiage.maintenance_follow_us)}
		</Text>

		<div class="sm-container">
			{#each socialMedia as { name, icon, link }}
				<a href={link} target="_blank" rel="noopener noreferrer" aria-label={name}>
					<div class={`sm-item ${useAlternative2 ? 'sm-item-alt2' : ''}`}>
						<span class={useAlternative2 ? 'sm-item-icon' : ''}>
							<svelte:component this={icon} />
						</span>
					</div>
				</a>
			{/each}
		</div>
	</div>
</Modal>

<style>
	.underlay {
		position: fixed;
		top: 0px;
		left: 0px;
		right: 0px;
		bottom: 0px;
		z-index: 10;
		--tw-bg-opacity: 1;
		background-color: rgb(0 0 0 / var(--tw-bg-opacity, 1));
		opacity: 0.8;
}

	.overlay {
		position: fixed;
		top: 0px;
		left: 0px;
		right: 0px;
		bottom: 0px;
		z-index: 20;
		display: flex;
		align-items: center;
		justify-content: center;
}

	.content {
		display: flex;
		height: 100%;
		width: 100%;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
		padding: 1rem;
		text-align: center;
}

	.center {
		margin-left: auto;
		margin-right: auto;
		width: 100%;
		max-width: 36rem;
}

	.modal {
		display: flex;
		width: 100%;
		flex-direction: column;
		align-items: center;
		border-radius: 0.5rem;
		padding-left: 1.5rem;
		padding-right: 1.5rem;
		padding-top: 2.5rem;
		padding-bottom: 2.5rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		--tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
		--tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
		box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
}

	.content {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
		text-align: center;
}

	.icon {
		width: auto;
		height: 300px;
	}

	.icon-mobile {
		width: auto;
		height: 230px;
	}

	.maintenance-markdown {
		width: 100%;
		max-width: 48rem;
		text-align: center;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	:global(.maintenance-markdown h1) {
		margin: 0px;
		margin-top: 0.25rem;
		margin-bottom: 1rem;
		font-size: 1.5rem;
		line-height: 2rem;
		font-weight: 600;
		line-height: 1.25;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	@media (min-width: 768px) {

		:global(.maintenance-markdown h1) {
				font-size: 1.875rem;
				line-height: 2.25rem;
		}
}

	:global(.maintenance-markdown h2) {
		margin: 0px;
		margin-bottom: 0.5rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 500;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--color-text-300);
}

	@media (min-width: 768px) {

		:global(.maintenance-markdown h2) {
				font-size: 1rem;
				line-height: 1.5rem;
		}
}

	:global(.maintenance-markdown p) {
		margin-bottom: 0.75rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		line-height: 1.625;
		color: var(--color-text-100);
}

	:global(.maintenance-markdown a) {
		color: var(--color-highlight-5);
}

	:global(.maintenance-markdown hr) {
		margin: 0px;
		height: 0px;
		border-width: 0px;
}

	.sm-container {
		margin-top: 1rem;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
}

	.sm-item {
		display: flex;
		height: 2.5rem;
		width: 2.5rem;
		align-items: center;
		justify-content: center;
		border-radius: 0.375rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--component-footer-social);
}

	.sm-item-alt2 {
		border-radius: 6px;
		background:
			radial-gradient(
				101.56% 79.71% at 21.62% 50.54%,
				rgba(255, 255, 255, 0.1) 0%,
				rgba(255, 255, 255, 0) 100%
			),
			radial-gradient(
				35.59% 81.32% at 50% -16.3%,
				rgba(255, 255, 255, 0.2) 0%,
				rgba(255, 255, 255, 0) 100%
			);
		background-blend-mode: plus-lighter, plus-lighter;
	}

	.sm-item-icon {
		opacity: 0.4;
	}

	.highlight {
		font-weight: 700;
		color: var(--color-primary-500);
}

	.bold {
		font-weight: 700;
}
</style>
